import React, { useEffect, useState } from "react";
import { MdPerson, MdEmail, MdPhone, MdCheckCircle } from "react-icons/md";
import { FiCalendar, FiTag, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Modal from "./Modal";

const PERMISSION_ICONS = {
  retroplanning: FiCalendar,
  cleaning: MdCheckCircle,
  hse: MdCheckCircle,
  badging: FiTag,
};

const inputCls =
  "w-full py-2.5 border border-gray-200 bg-gray-50/60 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:bg-white transition-colors";
const inputWithIconCls = `${inputCls} pl-10 pr-4`;
const inputPlainCls = `${inputCls} px-4`;
const labelCls = "block text-xs font-semibold text-gray-600 mb-1.5";
const cardCls = "bg-white border border-gray-200/80 rounded-2xl";
const btnPrimary =
  "inline-flex items-center gap-1.5 px-6 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:bg-indigo-800 transition-colors font-semibold text-sm shadow-sm shadow-indigo-600/20 disabled:opacity-60 disabled:cursor-not-allowed";
const btnSecondary =
  "inline-flex items-center gap-1.5 px-6 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors font-semibold text-sm";

const GRID_COLS = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" };

const ToggleSwitch = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ${checked ? "bg-indigo-600" : "bg-gray-300"}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-6" : "translate-x-1"}`}
    />
  </button>
);

const SectionCard = ({ title, subtitle, children, className = "" }) => (
  <div className={`${cardCls} p-5 ${className}`}>
    {title && (
      <div className="mb-4">
        <h4 className="font-semibold text-gray-800 text-sm">{title}</h4>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    )}
    {children}
  </div>
);

const QuotaPicker = ({ label, help, options, value, onChange, limitValue, onLimitChange, limitTrigger, limitLabel, limitPlaceholder }) => (
  <SectionCard title={label} subtitle={help}>
    <div className={`grid ${GRID_COLS[options.length] || "grid-cols-2"} gap-2 mb-3`}>
      {options.map((opt) => (
        <button
          type="button"
          key={opt}
          onClick={() => onChange(opt)}
          className={`py-2.5 rounded-xl border text-sm font-medium capitalize transition-all ${
            value === opt
              ? "border-indigo-600 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600/20"
              : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
    {value === limitTrigger && (
      <div className="pt-1">
        <label className={labelCls}>{limitLabel}</label>
        <input
          type="number"
          min="1"
          value={limitValue || ""}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          className={inputPlainCls}
          placeholder={limitPlaceholder}
        />
      </div>
    )}
  </SectionCard>
);

const AddUserModal = ({ isOpen, onClose, onSubmit, form, setForm, features = [], isSubmitting = false, mode = "create" }) => {
  const [step, setStep] = useState(1);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(e);
  };

  const steps = [
    { number: 1, title: "User Info" },
    { number: 2, title: "Usage Limits" },
    { number: 3, title: "Permissions" },
  ];
  const lastStep = steps.length;
  const progressPct = ((step - 1) / (steps.length - 1)) * 100;

  const eventQuota = form.eventQuota || { mode: "single", limit: 1 };
  const standsQuota = form.standsQuota || { mode: "unlimited", limit: null };

  const togglePermission = (key) => {
    const current = form.permissions || [];
    const updated = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    setForm({ ...form, permissions: updated });
  };

  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === "edit" ? "Edit User" : "Create User"} size="lg">
      <div className="flex flex-col h-full">

        {/* STEP INDICATOR */}
        <div className="px-6 pt-5 pb-4 bg-gray-50/80 border-b border-gray-200">
          <div className="relative">
            <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-200 rounded-full" />
            <div
              className="absolute top-4 left-4 h-0.5 bg-indigo-600 rounded-full transition-all duration-300"
              style={{ width: `calc((100% - 2rem) * ${progressPct / 100})` }}
            />
            <div className="relative flex justify-between">
              {steps.map((s) => (
                <div key={s.number} className="flex flex-col items-center gap-1.5 w-20">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-all bg-white ${
                      step > s.number
                        ? "border-indigo-600 bg-indigo-600 text-white"
                        : step === s.number
                          ? "border-indigo-600 text-indigo-600"
                          : "border-gray-200 text-gray-400"
                    }`}
                  >
                    {step > s.number ? <MdCheckCircle className="text-base" /> : s.number}
                  </div>
                  <span
                    className={`text-[11px] font-medium text-center leading-tight ${step === s.number ? "text-indigo-600" : "text-gray-400"}`}
                  >
                    {s.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-4 flex-1 overflow-y-auto">

          {/* ── STEP 1 — User Info ── */}
          {step === 1 && (
            <SectionCard title="Account Owner" subtitle="This person will log in and manage their own events.">
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MdPerson className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    <input
                      value={form.name || ""}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputWithIconCls}
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MdEmail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    <input
                      type="email"
                      value={form.email || ""}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={`${inputWithIconCls} ${mode === "edit" ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                      placeholder="Enter email address"
                      required
                      disabled={mode === "edit"}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    Phone <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                    <MdPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    <input
                      value={form.phone || ""}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={inputWithIconCls}
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
              </div>
            </SectionCard>
          )}

          {/* ── STEP 2 — Usage Limits ── */}
          {step === 2 && (
            <div className="space-y-4">
              <QuotaPicker
                label="Event Allowance"
                help="How many events this account can create for itself after logging in."
                options={["single", "multiple", "unlimited"]}
                value={eventQuota.mode}
                onChange={(m) => setForm({ ...form, eventQuota: { ...eventQuota, mode: m } })}
                limitTrigger="multiple"
                limitLabel="How Many Events"
                limitPlaceholder="e.g. 3"
                limitValue={eventQuota.limit}
                onLimitChange={(v) => setForm({ ...form, eventQuota: { ...eventQuota, limit: v } })}
              />

              <QuotaPicker
                label="Stands Per Event"
                help="How many stands can be added within each of their events."
                options={["limited", "unlimited"]}
                value={standsQuota.mode}
                onChange={(m) => setForm({ ...form, standsQuota: { ...standsQuota, mode: m } })}
                limitTrigger="limited"
                limitLabel="Stands Limit"
                limitPlaceholder="e.g. 20"
                limitValue={standsQuota.limit}
                onLimitChange={(v) => setForm({ ...form, standsQuota: { ...standsQuota, limit: v } })}
              />
            </div>
          )}

          {/* ── STEP 3 — Permissions ── */}
          {step === 3 && (
            <SectionCard title="Feature Access" subtitle="Choose which modules this user can access.">
              {features.length === 0 ? (
                <p className="text-sm text-gray-400">No features configured yet — add them in Billing → Pricing.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {features.map((feature) => {
                    const active = (form.permissions || []).includes(feature.key);
                    const Icon = PERMISSION_ICONS[feature.key] || FiTag;
                    return (
                      <button
                        type="button"
                        key={feature.key}
                        onClick={() => togglePermission(feature.key)}
                        className={`text-left p-4 rounded-xl border transition-all ${active ? "border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600/20" : "border-gray-200 bg-white hover:border-gray-300"}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"}`}>
                              <Icon size={16} />
                            </div>
                            <div className="min-w-0">
                              <p className={`text-sm font-semibold ${active ? "text-indigo-900" : "text-gray-800"}`}>{feature.name}</p>
                              {feature.description && (
                                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{feature.description}</p>
                              )}
                              <p className="text-xs text-gray-400 mt-0.5">{feature.price} MAD</p>
                            </div>
                          </div>
                          <ToggleSwitch checked={active} onChange={() => togglePermission(feature.key)} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          )}

        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-200 flex justify-between items-center">
          <button type="button" onClick={step > 1 ? () => setStep(step - 1) : onClose} className={btnSecondary}>
            {step > 1 && <FiChevronLeft size={16} />}
            {step > 1 ? "Back" : "Cancel"}
          </button>

          {step < lastStep ? (
            <button type="button" onClick={() => setStep(step + 1)} className={btnPrimary}>
              Next
              <FiChevronRight size={16} />
            </button>
          ) : (
            <button type="button" onClick={handleSubmit} disabled={isSubmitting} className={btnPrimary}>
              {isSubmitting ? (
                mode === "edit" ? "Saving..." : "Creating..."
              ) : (
                <>
                  <MdCheckCircle size={16} />
                  {mode === "edit" ? "Save Changes" : "Create User"}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddUserModal;